import React from 'react';
import { CheckCircle, Clock, Mail, ArrowRight, Home } from 'lucide-react';
import { Button } from '../ui/Button';

export interface RegistrationSuccessProps {
  firstName?: string;
  email?: string;
  registeredAt?: string;
  onGoHome?: () => void;
}

const RegistrationSuccess: React.FC<RegistrationSuccessProps> = ({
  firstName = 'there',
  email = '',
  registeredAt,
  onGoHome
}) => {

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    }
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:admin@reddfamily.org?subject=Registration Question';
  };

  return (
    <div className="min-h-screen bg-surface-0 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 border border-border rounded-full"></div>
        <div className="absolute bottom-40 right-20 w-24 h-24 border border-border rounded-full"></div>
        <div className="absolute top-1/3 right-10 w-16 h-16 bg-brand-light rounded-full"></div>
        <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-brand-light rounded-full"></div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full">
          <div className="bg-surface-1 backdrop-blur-sm rounded-2xl shadow-sm border border-border p-8">

            {/* Success Icon */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                <CheckCircle className="w-10 h-10 text-white" aria-hidden="true" />
              </div>

              <h1 className="text-3xl font-bold text-text-primary font-serif mb-3">
                Registration Received!
              </h1>

              <p className="text-text-secondary text-lg">
                Welcome to the Redd Family, {firstName}!
              </p>
            </div>

            {/* Main Message */}
            <div className="bg-surface-0 border border-border rounded-lg p-6 mb-6">
              <div className="flex items-start space-x-4">
                <Clock className="w-6 h-6 text-brand mt-1 flex-shrink-0" aria-hidden="true" />
                <div>
                  <h2 className="font-semibold text-text-primary mb-2">
                    Your application is under review
                  </h2>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    Thank you for registering with the Redd Family Association!
                    Your application has been received and will be reviewed by a
                    family administrator within <strong>24-48 hours</strong>.
                  </p>
                </div>
              </div>
            </div>

            {/* What Happens Next */}
            <div className="mb-6">
              <h3 className="font-semibold text-text-primary mb-4 flex items-center">
                <ArrowRight className="w-5 h-5 mr-2 text-brand" aria-hidden="true" />
                What happens next:
              </h3>
              <ul className="space-y-3 text-sm text-text-secondary">
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-brand rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  A family administrator will review your registration details
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-brand rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  You'll receive an email confirmation once your account is approved
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-brand rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  After approval, you can log in and access all family resources
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-brand rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Check your spam folder if you don't see our email
                </li>
              </ul>
            </div>

            {/* Contact Information */}
            <div className="bg-surface-0 border border-border rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-text-tertiary mt-1 flex-shrink-0" aria-hidden="true" />
                <div>
                  <h4 className="font-medium text-text-primary text-sm">Questions or concerns?</h4>
                  <p className="text-text-secondary text-sm mt-1">
                    Contact us at{' '}
                    <button
                      onClick={handleContactSupport}
                      className="text-brand hover:text-brand underline focus:outline-none focus:ring-2 focus:ring-brand rounded"
                    >
                      admin@reddfamily.org
                    </button>
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3">
              <Button
                onClick={handleGoHome}
                variant="primary"
                size="lg"
                fullWidth
                icon={<Home className="w-4 h-4" />}
              >
                Return to Homepage
              </Button>
            </div>

            {/* Registration Details (if provided) */}
            {(email || registeredAt) && (
              <div className="mt-6 pt-6 border-t border-border">
                <h4 className="font-medium text-text-secondary text-sm mb-2">Registration Details:</h4>
                <div className="text-xs text-text-tertiary space-y-1">
                  {email && <p><strong>Email:</strong> {email}</p>}
                  {registeredAt && (
                    <p>
                      <strong>Registered:</strong>{' '}
                      {new Date(registeredAt).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Heritage Quote */}
          <div className="text-center mt-8 pt-6">
            <p className="text-xs text-text-tertiary italic">
              "A family that prays together, stays together"
            </p>
            <p className="text-xs text-text-tertiary mt-1">
              - The Redd Family Motto
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export { RegistrationSuccess };
